import { RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { Toaster } from 'sonner';

// Import styles first
import '@ui/components/styles.css';

// Import the generated route tree
import { QueryProvider } from './providers/QueryProvider';
import { routeTree } from './routeTree.gen';

import { SidebarProvider } from '@ui/components/sidebar';
import { ThemeProvider } from '@ui/components/theme-provider';
import { ApiProvider } from './hooks/useApiClient';
import { AuthProvider, useAuth } from './hooks/useAuth';
import reportWebVitals from './reportWebVitals';

export interface AppContext {
  auth?: ReturnType<typeof useAuth>;
}

// Create a new router instance
const router = createRouter({
  routeTree,
  context: { auth: undefined },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
});

function InnerApp() {
  const auth = useAuth();
  return (
    <RouterProvider
      router={router}
      context={{
        auth,
      }}
    />
  );
}

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Render the app
const rootElement = document.getElementById('app');
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <QueryProvider>
        <ThemeProvider
          attribute="class"
          defaultTheme="system"
          enableSystem
          disableTransitionOnChange
        >
          <AuthProvider>
            <ApiProvider>
              <SidebarProvider>
                <InnerApp />
                <Toaster />
              </SidebarProvider>
            </ApiProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryProvider>
    </StrictMode>
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
